import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService } from './baseApi';

export interface SalesByCategory {
  categoryId?: number;
  categoryName: string;
  /** New backend shape */
  netSalesAmount?: number;
  taxAmount?: number;
  grossSalesAmount?: number;
  /** Backward compatible */
  totalSalesValue?: number;
  invoiceCount?: number;
}

export interface SalesByCustomer {
  customerName: string;
  /** New backend shape */
  gstin?: string;
  pan?: string;
  netSalesAmount?: number;
  taxAmount?: number;
  grossSalesAmount?: number;
  /** Backward compatible */
  totalSalesValue?: number;
  invoiceCount?: number;
}

export interface TopProductBySales {
  endgoodId?: number;
  itemName?: string;
  categoryId?: number;
  categoryName?: string;
  /** New backend shape */
  netSalesAmount?: number;
  taxAmount?: number;
  grossSalesAmount?: number;
  totalBilledQuantity?: number;
  /** Backward compatible */
  productName?: string;
  totalSalesValue?: number;
  quantity?: number;
}

export interface ProductActivity {
  productId?: number;
  productName: string;
  lastInvoiceDate?: string | null;
  isActive?: boolean;
  invoiceCountLastNDays?: number;
  totalSalesValueLastNDays?: number;
}

class SalesDashboardApiService extends BaseApiService {
  private async unwrapArray<T>(res: any): Promise<T[]> {
    if (Array.isArray(res)) return res as T[];
    if (res && Array.isArray(res.data)) return res.data as T[];
    return [];
  }

  async getSalesByCategory(params: { branchId?: number; startDate?: string; endDate?: string }): Promise<SalesByCategory[]> {
    try {
      const res = await this.makeRequest<any>(API_ENDPOINTS.SALES_DASHBOARD.BY_CATEGORY(params.branchId, params.startDate, params.endDate), {
        method: HTTP_METHODS.GET,
      });
      return await this.unwrapArray<SalesByCategory>(res);
    } catch (e) {
      toast.error('Failed to load sales by category');
      throw e;
    }
  }

  async getSalesByCustomer(params: { branchId?: number; startDate?: string; endDate?: string }): Promise<SalesByCustomer[]> {
    try {
      const res = await this.makeRequest<any>(API_ENDPOINTS.SALES_DASHBOARD.BY_CUSTOMER(params.branchId, params.startDate, params.endDate), {
        method: HTTP_METHODS.GET,
      });
      return await this.unwrapArray<SalesByCustomer>(res);
    } catch (e) {
      toast.error('Failed to load sales by customer');
      throw e;
    }
  }

  async getTopProducts(params: { branchId?: number; startDate?: string; endDate?: string; limit?: number }): Promise<TopProductBySales[]> {
    try {
      const res = await this.makeRequest<any>(
        API_ENDPOINTS.SALES_DASHBOARD.TOP_PRODUCTS(params.branchId, params.startDate, params.endDate, params.limit ?? 10),
        { method: HTTP_METHODS.GET }
      );
      return await this.unwrapArray<TopProductBySales>(res);
    } catch (e) {
      toast.error('Failed to load top products');
      throw e;
    }
  }

  async getProductActivity(params: { branchId?: number; days?: number }): Promise<ProductActivity[]> {
    try {
      const res = await this.makeRequest<any>(
        API_ENDPOINTS.SALES_DASHBOARD.PRODUCT_ACTIVITY(params.branchId, params.days ?? 30),
        { method: HTTP_METHODS.GET }
      );
      return await this.unwrapArray<ProductActivity>(res);
    } catch (e) {
      toast.error('Failed to load product activity');
      throw e;
    }
  }
}

export const salesDashboardApiService = new SalesDashboardApiService();

